import { useState } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppProvider }  from "./context/AppContext";
import LoginPage      from "./pages/LoginPage";
import ForgotPassword from "./pages/ForgotPassword";
import RegisterPage   from "./pages/RegisterPage";
import ApplicantForm  from "./pages/ApplicantForm";
import SuccessPage    from "./pages/SuccessPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAgents    from "./pages/AdminAgents";
import AdminService   from "./pages/AdminService";
import AdminReports   from "./pages/AdminReports";
import AdminSettings  from "./pages/AdminSettings";
import AdminView      from "./pages/AdminView";
import AgentDashboard from "./pages/AgentDashboard";
import AgentView      from "./pages/AgentView";
import ApplicantReview from "./pages/ApplicantReview";

// If the URL contains ?page=applicant-review&app_id=XXXX (a shared review link),
// open that page directly instead of the login screen — no login required.
function initialRoute() {
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("page") === "applicant-review" && qs.get("app_id")) {
      return { page: "applicant-review", params: { app_id: qs.get("app_id") } };
    }
  } catch {}
  return { page: "login", params: {} };
}

// Waits for the saved login session to be checked against the server before
// rendering anything — avoids a flash of the login screen on every refresh.
function Gate({ children }) {
  const { ready } = useAuth();
  if (!ready) {
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#f0f3f9",fontFamily:"'DM Sans',sans-serif",color:"#6b7a90",fontSize:14}}>
        Loading…
      </div>
    );
  }
  return children;
}

export default function App() {
  const start = initialRoute();
  const [page,   setPage]   = useState(start.page);
  const [params, setParams] = useState(start.params);

  const navigate = (to, p={}) => { setPage(to); setParams(p); window.scrollTo(0,0); };

  const render = () => {
    switch(page) {
      case "login":       return <LoginPage      navigate={navigate}/>;
      case "forgot":      return <ForgotPassword navigate={navigate}/>;
      case "register":    return <RegisterPage   navigate={navigate}/>;
      case "form":        return <ApplicantForm  navigate={navigate}/>;
      case "success":     return <SuccessPage    navigate={navigate} params={params}/>;
      case "dashboard":   return <AdminDashboard navigate={navigate}/>;
      case "agents":      return <AdminAgents    navigate={navigate}/>;
      case "air-tickets": return <AdminService   navigate={navigate} service="Air Tickets" icon="✈️"/>;
      case "hotels":      return <AdminService   navigate={navigate} service="Hotels"      icon="🏨"/>;
      case "visas":       return <AdminService   navigate={navigate} service="Visas"       icon="🛂"/>;
      case "reports":     return <AdminReports   navigate={navigate}/>;
      case "settings":    return <AdminSettings  navigate={navigate}/>;
      case "view":        return <AdminView      navigate={navigate} params={params}/>;
      case "agent-dashboard": return <AgentDashboard navigate={navigate}/>;
      case "agent-view":  return <AgentView      navigate={navigate} params={params}/>;
      case "applicant-review": return <ApplicantReview params={params}/>;
      default:            return <LoginPage      navigate={navigate}/>;
    }
  };

  // The shared review link doesn't need a session at all — skip the gate for it.
  if (page === "applicant-review") {
    return <AuthProvider navigate={navigate}><AppProvider>{render()}</AppProvider></AuthProvider>;
  }

  return (
    <AuthProvider navigate={navigate}>
      <Gate><AppProvider>{render()}</AppProvider></Gate>
    </AuthProvider>
  );
}
